import { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import { format, addMonths, startOfMonth, endOfMonth, isSameDay } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { apiService } from '@/lib/api';
import { Calendar, Ban, CheckCircle, Loader2, X } from 'lucide-react';
import 'react-day-picker/style.css';

interface RoomAvailabilityCalendarProps {
  roomId: string;
  roomNumber: string;
  companyId: string;
  onClose: () => void;
}

export default function RoomAvailabilityCalendar({
  roomId,
  roomNumber,
  onClose,
}: RoomAvailabilityCalendarProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [blockedDates, setBlockedDates] = useState<Date[]>([]);
  const [reservedDates, setReservedDates] = useState<Date[]>([]);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const handleClose = () => {
    setOpen(false);
    setTimeout(onClose, 100); // Small delay for animation
  };

  // Load availability data for the current month
  const loadAvailability = async (month: Date) => {
    setLoading(true);
    try {
      const startDate = format(startOfMonth(month), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(addMonths(month, 2)), 'yyyy-MM-dd'); // Load 3 months

      const data = await apiService.getRoomAvailabilityCalendar(roomId, startDate, endDate);

      // Parse blocked and reserved dates
      const blocked: Date[] = [];
      const reserved: Date[] = [];

      data.forEach((item: any) => {
        const date = new Date(item.date);
        if (item.status === 'BLOCKED') {
          blocked.push(date);
        } else if (item.status === 'RESERVED') {
          reserved.push(date);
        }
      });

      setBlockedDates(blocked);
      setReservedDates(reserved);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load availability',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAvailability(currentMonth);
  }, [roomId, currentMonth]);

  const handleMonthChange = (month: Date) => {
    setCurrentMonth(month);
  };

  const handleDayClick = (day: Date) => {
    // Check if date is already reserved
    const isReserved = reservedDates.some(d => isSameDay(d, day));
    if (isReserved) {
      toast({
        title: 'Cannot Block',
        description: 'This date has an active reservation',
        variant: 'destructive',
      });
      return;
    }

    // Toggle selection
    const isSelected = selectedDates.some(d => isSameDay(d, day));
    if (isSelected) {
      setSelectedDates(selectedDates.filter(d => !isSameDay(d, day)));
    } else {
      setSelectedDates([...selectedDates, day]);
    }
  };

  const handleBlockDates = () => {
    if (selectedDates.length === 0) {
      toast({
        title: 'No Dates Selected',
        description: 'Please select dates to block',
        variant: 'destructive',
      });
      return;
    }
    setShowBlockDialog(true);
  };

  const handleUnblockDates = async () => {
    if (selectedDates.length === 0) {
      toast({
        title: 'No Dates Selected',
        description: 'Please select dates to unblock',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // Sort dates and get range
      const sortedDates = selectedDates.sort((a, b) => a.getTime() - b.getTime());
      const startDate = format(sortedDates[0], 'yyyy-MM-dd');
      // Add 1 day to end date to make it inclusive (backend uses lt: end)
      const endDateObj = new Date(sortedDates[sortedDates.length - 1]);
      endDateObj.setDate(endDateObj.getDate() + 1);
      const endDate = format(endDateObj, 'yyyy-MM-dd');

      await apiService.blockRoomDates(roomId, {
        startDate,
        endDate,
        status: 'AVAILABLE',
      });

      toast({
        title: 'Success',
        description: `Unblocked ${selectedDates.length} date(s)`,
      });

      setSelectedDates([]);
      await loadAvailability(currentMonth);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to unblock dates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const confirmBlockDates = async () => {
    setLoading(true);
    try {
      // Sort dates and get range
      const sortedDates = selectedDates.sort((a, b) => a.getTime() - b.getTime());
      const startDate = format(sortedDates[0], 'yyyy-MM-dd');
      // Add 1 day to end date to make it inclusive (backend uses lt: end)
      const endDateObj = new Date(sortedDates[sortedDates.length - 1]);
      endDateObj.setDate(endDateObj.getDate() + 1);
      const endDate = format(endDateObj, 'yyyy-MM-dd');

      await apiService.blockRoomDates(roomId, {
        startDate,
        endDate,
        reason: blockReason,
        status: 'BLOCKED',
      });

      toast({
        title: 'Success',
        description: `Blocked ${selectedDates.length} date(s)`,
      });

      setSelectedDates([]);
      setBlockReason('');
      setShowBlockDialog(false);
      await loadAvailability(currentMonth);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to block dates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const modifiers = {
    blocked: blockedDates,
    reserved: reservedDates,
    selected: selectedDates,
  };

  const modifiersStyles = {
    blocked: {
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: 'bold',
    },
    reserved: {
      backgroundColor: '#c39d76', // Brand primary color
      color: 'white',
    },
    selected: {
      backgroundColor: '#000000', // Black
      color: 'white',
      fontWeight: 'bold',
    },
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Room {roomNumber} - Block Dates
              </DialogTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-6 w-6 rounded-full"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-sm bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-white border-2 border-gray-300 rounded"></div>
                <span className="text-gray-700">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: '#c39d76' }}></div>
                <span className="text-gray-700">Reserved</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-gray-700">Blocked</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-black rounded"></div>
                <span className="text-gray-700">Selected</span>
              </div>
            </div>

            {/* Calendar */}
            <div className="flex justify-center bg-white">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : (
                <div className="room-availability-calendar">
                  <style>{`
                    .room-availability-calendar .rdp-day {
                      border: 1px solid #e5e7eb;
                      margin: 1px;
                    }
                    .room-availability-calendar .rdp-day:hover {
                      border-color: #9ca3af;
                    }
                    .room-availability-calendar .rdp-day_button {
                      border-radius: 4px;
                    }
                  `}</style>
                  <DayPicker
                    mode="multiple"
                    selected={selectedDates}
                    onDayClick={handleDayClick}
                    month={currentMonth}
                    onMonthChange={handleMonthChange}
                    modifiers={modifiers}
                    modifiersStyles={modifiersStyles}
                    numberOfMonths={2}
                    disabled={{ before: new Date() }}
                  />
                </div>
              )}
            </div>

            {/* Selected dates info */}
            {selectedDates.length > 0 && (
              <div className="p-3 bg-gray-100 border border-gray-300 rounded-lg">
                <p className="text-sm font-medium text-gray-900">
                  {selectedDates.length} date(s) selected
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 justify-end pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Close
              </Button>
              <Button
                variant="outline"
                onClick={handleUnblockDates}
                disabled={loading || selectedDates.length === 0}
                className="flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" />
                Unblock Selected
              </Button>
              <Button
                onClick={handleBlockDates}
                disabled={loading || selectedDates.length === 0}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
              >
                <Ban className="w-4 h-4" />
                Block Selected
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Block confirmation dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block Dates</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Dates to Block</Label>
              <p className="text-sm text-gray-600 mt-1">
                {selectedDates.length} date(s) selected
              </p>
            </div>

            <div>
              <Label htmlFor="blockReason">Reason (Optional)</Label>
              <Textarea
                id="blockReason"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="e.g., Maintenance, Renovation, etc."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBlockDialog(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmBlockDates}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Blocking...
                </>
              ) : (
                'Confirm Block'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
